"use client";

import { c as _c } from "react/compiler-runtime";
import { EventEnum } from "@sprawdzonabron/shared";
import Link from "next/link";
import { useEffect, useMemo, useState } from "react";
import { useAnalyticsEvent } from "../../../../../api/hooks/useAnalyticsEvent";
import { BigStatCard } from "./big-stat-card";
import { FunFact } from "./fun-fact";
import { Icons } from "./icons";
import { SectionTitle } from "./section-title";
import { StatBar } from "./stat-bar";

// --- WIZARD STEPS CONTENT ---

const getShooterSteps = (ctaAnalyticalEvent: (info: string) => void) => [{
  title: "Wstęp",
  content: <div className="space-y-6">
        <div className="bg-white dark:bg-slate-850 rounded-2xl p-8 border border-slate-200 dark:border-slate-800">
          <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">
            Wstęp do badania
          </h3>
          <p className="text-slate-600 dark:text-slate-300 text-base leading-relaxed">
            Ten raport powstał dzięki szerokiemu odzewowi z grup{" "}
            <strong>BROŃ PALNA - od zera po pozwolenie (i więcej)</strong> oraz{" "}
            <strong>AR-15 Polska</strong>. Ankietowani to nie są "niedzielni" hobbyści – to grupa,
            która aktywnie kupuje, strzela i wydaje pieniądze. Wyniki potwierdzają to, co czujemy na
            co dzień: proces zakupowy w Internecie to często droga przez mękę.
          </p>
          <div className="mt-8 grid grid-cols-2 gap-4">
            <div className="text-center p-4 bg-slate-100 dark:bg-slate-900 rounded-lg">
              <div className="text-3xl font-black text-brand-600 dark:text-brand-500">800+</div>
              <div className="text-xs text-slate-500 dark:text-slate-500 uppercase font-bold">
                Ankietowanych
              </div>
            </div>
            <div className="text-center p-4 bg-slate-100 dark:bg-slate-900 rounded-lg">
              <div className="text-3xl font-black text-brand-600 dark:text-brand-500">100%</div>
              <div className="text-xs text-slate-500 dark:text-slate-500 uppercase font-bold">
                Mężczyźni
              </div>
            </div>
          </div>
        </div>
        <FunFact>
          Według danych Komendy Głównej Policji, rok 2024 był rekordowy pod względem wydanych
          pozwoleń (ponad 45 tysięcy nowych decyzji). Rynek rośnie dynamicznie, a nowi adepci
          szukają informacji głównie w sieci.
        </FunFact>
      </div>
}, {
  title: "Demografia",
  content: <div className="space-y-6">
        <SectionTitle>Kim jest statystyczny strzelec?</SectionTitle>
        <p className="text-slate-500 dark:text-slate-400 mb-6">
          Analiza stażu (pyt. 1) oraz wielkości arsenału (pyt. 2) pokazuje obraz doświadczonego i
          dobrze uzbrojonego klienta.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <BigStatCard label="Doświadczenie" value="82%" subtext="Posiada broń od ponad roku. Największa grupa (40%) to staż 1-3 lata." icon={Icons.User} />
          <BigStatCard label="Uzbrojenie" value="43%" subtext="Ma w szafie od 3 do 7 sztuk broni. Aż 28% to kolekcje 8-15 sztuk." icon={Icons.Chart} />
          <BigStatCard label="Aktywność" value="46%" subtext="Kupiło broń w ostatnich 6 miesiącach. To nie jest jednorazowy zakup." icon={Icons.Shop} />
          <BigStatCard label="Budżet" value="4-8k" subtext="63% ankietowanych wydaje średnio tyle na jedną jednostkę broni." icon={() => <span className="text-xl font-bold">PLN</span>} />
        </div>
        <FunFact>
          Średni koszt "wejścia" w hobby (pozwolenie, szafa S1, pierwsza broń, amunicja) przekracza
          często 5000 zł. Mimo to, 40% ankietowanych kupuje nową jednostkę 2-3 razy w roku.
        </FunFact>
      </div>
}, {
  title: "Proces",
  content: <div className="space-y-6">
        <SectionTitle>Piekło otwartych kart</SectionTitle>
        <div className="bg-white dark:bg-slate-850 p-6 rounded-xl border border-slate-200 dark:border-slate-800">
          <p className="text-slate-500 dark:text-slate-400 mb-6">
            Zapytaliśmy, jak wygląda Wasz proces szukania broni. Wyniki są miażdżące dla obecnych
            rozwiązań e-commerce.
          </p>
          <StatBar label="Szuka wpisując model w Google i otwierając dziesiątki kart" percentage={88} colorClass="bg-red-500" />
          <StatBar label="Opiera decyzje na YouTube" percentage={65} colorClass="bg-slate-400 dark:bg-slate-600" />
          <StatBar label="Opiera decyzje na Forach/Grupach" percentage={57} colorClass="bg-slate-400 dark:bg-slate-600" />
          <StatBar label="Jedzie do sklepu 'w ciemno' po to co jest" percentage={7} colorClass="bg-slate-300 dark:bg-slate-700" />
        </div>
        <FunFact>
          Zjawisko ROPO (Research Online, Purchase Offline) w branży zbrojeniowej jest ekstremalne.
          Decyzja zapada przed ekranem komputera w 93% przypadków (tzw. Zero Moment of Truth wg
          Google), a wizyta w sklepie to tylko formalność.
        </FunFact>
      </div>
}, {
  title: "Problemy",
  content: <div className="space-y-6">
        <SectionTitle>Co nas najbardziej wkurza?</SectionTitle>
        <p className="text-slate-500 dark:text-slate-400 mb-6">
          Zidentyfikowaliśmy 3 główne "bóle" na podstawie pytań otwartych i wielokrotnego wyboru.
        </p>
        <div className="grid gap-4">
          <div className="flex items-start gap-4 bg-danger-900/10 dark:bg-danger-900/20 p-4 rounded-xl border border-danger-500/30 dark:border-danger-500/30">
            <div className="mt-1 text-danger-500">
              <Icons.Warning />
            </div>
            <div>
              <h4 className="font-bold text-slate-900 dark:text-white">1. Widmo-towar (65%)</h4>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                Największa frustracja. Na stronie WWW status "dostępny", po przyjeździe okazuje się,
                że błąd systemu.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-4 bg-slate-100 dark:bg-slate-800/50 p-4 rounded-xl border border-slate-200 dark:border-slate-700">
            <div className="mt-1 text-slate-500 dark:text-slate-400">
              <Icons.Car />
            </div>
            <div>
              <h4 className="font-bold text-slate-900 dark:text-white">
                2. Turystyka sklepowa (45%)
              </h4>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                Konieczność dzwonienia do 10 sklepów, żeby znaleźć ten jeden, który ma Glocka na
                stanie.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-4 bg-slate-100 dark:bg-slate-800/50 p-4 rounded-xl border border-slate-200 dark:border-slate-700">
            <div className="mt-1 text-slate-500 dark:text-slate-400 font-mono font-bold">?</div>
            <div>
              <h4 className="font-bold text-slate-900 dark:text-white">3. Brak ceny (34%)</h4>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                "Zadzwoń o cenę" to bariera, której 35% klientów w ogóle nie przekracza - wolą iść
                do konkurencji.
              </p>
            </div>
          </div>
        </div>
        <FunFact>
          Badania e-commerce (np. Baymard Institute) wskazują, że 17% porzuceń koszyka wynika z
          braku przejrzystości kosztów. W branży broni, brak ceny to niemal gwarancja utraty klienta
          introwertycznego.
        </FunFact>
      </div>
}, {
  title: "Podsumowanie",
  content: <div className="space-y-8 text-center">
        <SectionTitle>Wnioski: Czego oczekujecie?</SectionTitle>
        <p className="text-slate-500 dark:text-slate-400">
          Ankieta jasno wskazała "Must Have" dla nowej platformy.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-left">
          <div className="p-6 bg-slate-100 dark:bg-slate-800 rounded-xl border border-brand-200 dark:border-brand-500/30">
            <div className="text-4xl font-black text-brand-600 dark:text-brand-400 mb-2 font-mono">
              74%
            </div>
            <div className="font-bold text-slate-900 dark:text-white uppercase text-sm">
              Porównywarka cen
            </div>
          </div>
          <div className="p-6 bg-slate-100 dark:bg-slate-800 rounded-xl border border-brand-200 dark:border-brand-500/30">
            <div className="text-4xl font-black text-brand-600 dark:text-brand-400 mb-2 font-mono">
              71%
            </div>
            <div className="font-bold text-slate-900 dark:text-white uppercase text-sm">
              Lokalizacja
            </div>
          </div>
          <div className="p-6 bg-slate-100 dark:bg-slate-800 rounded-xl border border-brand-200 dark:border-brand-500/30">
            <div className="text-4xl font-black text-brand-600 dark:text-brand-400 mb-2 font-mono">
              71%
            </div>
            <div className="font-bold text-slate-900 dark:text-white uppercase text-sm">
              Pewność stanów
            </div>
          </div>
        </div>

        <div className="mt-12 p-8 bg-gradient-to-b from-slate-100 to-white dark:from-slate-800 dark:to-slate-900 rounded-2xl border border-slate-200 dark:border-slate-700">
          <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-4">
            Mamy rozwiązanie Twoich problemów
          </h3>
          <p className="text-slate-500 dark:text-slate-400 mb-8 max-w-2xl mx-auto">
            SprawdzonaBron.pl to odpowiedź na wyniki tej ankiety. Agregujemy stany magazynowe z 80+
            sklepów w jednym miejscu.
          </p>
          <Link href="/" onClick={() => ctaAnalyticalEvent("find-gun")} className="inline-flex items-center gap-2 bg-brand-600 hover:bg-brand-500 text-white font-bold py-4 px-8 rounded-xl shadow-lg shadow-brand-500/20 transition-all hover:scale-105 group text-lg">
            <Icons.Search />
            <span>Znajdź Broń na SprawdzonaBron.pl</span>
            <Icons.ArrowRight className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </div>
}];
const getShopSteps = (ctaAnalyticalEvent: (info: string) => void) => [{
  title: "Wstęp B2B",
  content: <div className="space-y-6">
        <div className="bg-white dark:bg-slate-850 rounded-2xl p-8 border border-gold-200 dark:border-gold-500/20 relative overflow-hidden">
          <div className="absolute top-0 right-0 p-8 opacity-5 text-gold-500 transform translate-x-1/4 -translate-y-1/4">
            <Icons.Chart />
          </div>
          <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-2 flex items-center gap-2">
            <span className="text-gold-500">★</span> Niewidoczny Klient
          </h3>
          <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed max-w-3xl relative z-10">
            Przebadaliśmy ponad 800 aktywnych strzelców (Twoich potencjalnych klientów). Wnioski?
            Klient jest mobilny i ma budżet, ale ucieka, gdy napotyka bariery na stronie www. Ten
            raport pokazuje, gdzie tracisz pieniądze.
          </p>
        </div>
        <FunFact>
          Koszt pozyskania nowego klienta (CAC) jest od 5 do 25 razy wyższy niż utrzymanie obecnego.
          Tymczasem 65% Twoich potencjalnych klientów rezygnuje na etapie przeglądania strony z
          powodu braku informacji.
        </FunFact>
      </div>
}, {
  title: "Utracona Sprzedaż",
  content: <div className="space-y-6">
        <SectionTitle>Dlaczego tracisz sprzedaż?</SectionTitle>
        <p className="text-slate-500 dark:text-slate-400 mb-4">
          Wyniki z pytań dotyczących powodów rezygnacji z zakupu w konkretnym sklepie.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <BigStatCard label="Rezygnacja z zakupu" value="65%" subtext="Tyle strzelców porzuca stronę sklepu, gdy nie widzi ceny lub jasnego statusu dostępności." icon={Icons.Warning} theme="danger" />
          <BigStatCard label="Niechęć do telefonu" value="35%" subtext="Tylko 1/3 klientów zdecyduje się zadzwonić. Reszta (introwertycy, zajęci, wygodni) pójdzie tam, gdzie cena jest podana." icon={Icons.User} theme="gold" />
        </div>
        <FunFact>
          W psychologii sprzedaży znane jest zjawisko "tarcia" (friction). Każdy dodatkowy krok (jak
          konieczność wykonania telefonu) drastycznie obniża konwersję. Dla pokolenia wychowanego na
          Allegro/Amazon, brak ceny to dyskwalifikacja.
        </FunFact>
      </div>
}, {
  title: "Geografia",
  content: <div className="space-y-6">
        <SectionTitle>Mit lokalnego klienta</SectionTitle>
        <div className="bg-white dark:bg-slate-850 p-8 rounded-xl border border-slate-200 dark:border-slate-800">
          <p className="text-slate-500 dark:text-slate-400 mb-6">
            Czy warto pozycjonować się tylko lokalnie? Wyniki ankiety mówią jasno: NIE.
          </p>
          <StatBar label="Klienci szukający tylko lokalnie (<20km)" percentage={3} colorClass="bg-slate-300 dark:bg-slate-600" />
          <StatBar label="Gotowi przejechać >100km po towar" percentage={57} colorClass="bg-brand-500" />
          <StatBar label="Kupią w nieznanym małym sklepie (jeśli ma towar)" percentage={85} colorClass="bg-gold-500" />
        </div>
        <FunFact>
          Polska jest specyficznym rynkiem. Ze względu na rzadką sieć sklepów specjalistycznych,
          strzelcy są przyzwyczajeni do "wypraw" po broń. 100 km to dla 57% ankietowanych
          akceptowalny dystans, jeśli masz towar "od ręki".
        </FunFact>
      </div>
}, {
  title: "Portfel",
  content: <div className="space-y-6">
        <SectionTitle>Portfel Twojego Klienta</SectionTitle>
        <p className="text-slate-500 dark:text-slate-400">
          Analiza budżetu i czynników decyzyjnych.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 bg-slate-100 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-700">
            <div className="text-gold-500 dark:text-gold-400 font-mono text-xl font-bold mb-1">
              46%
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-400">
              Kupiło broń w ostatnim półroczu. To klient, który szybko wraca.
            </div>
          </div>
          <div className="p-4 bg-slate-100 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-700">
            <div className="text-gold-500 dark:text-gold-400 font-mono text-xl font-bold mb-1">
              63%
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-400">
              Budżet 4-8 tys. zł na jedną sztukę.
            </div>
          </div>
          <div className="p-4 bg-slate-100 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-700">
            <div className="text-gold-500 dark:text-gold-400 font-mono text-xl font-bold mb-1">
              30%
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-400">
              Decyduje "od ręki". Dla nich dostępność wygrywa z niższą ceną.
            </div>
          </div>
        </div>
        <FunFact>
          Dostępność towaru staje się nową walutą. W dobie przerwanych łańcuchów dostaw, klient woli
          zapłacić 200 zł więcej, ale wyjść ze sklepu z bronią, niż czekać 3 miesiące na "tańszą"
          dostawę.
        </FunFact>
      </div>
}, {
  title: "Wnioski B2B",
  content: <div className="space-y-8 text-center">
        <SectionTitle>Kluczowe wnioski dla Biznesu</SectionTitle>
        <div className="grid gap-4 text-left">
          <div className="bg-slate-100 dark:bg-slate-900 border-l-4 border-gold-400 dark:border-gold-500 p-5 rounded-r-xl">
            <h4 className="font-bold text-slate-900 dark:text-white text-sm uppercase">
              Transparentność sprzedaje
            </h4>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              Ukrywanie cen ("Zadzwoń") to strata 65% leadów. Klient internetowy chce konkretów.
            </p>
          </div>
          <div className="bg-slate-100 dark:bg-slate-900 border-l-4 border-gold-400 dark:border-gold-500 p-5 rounded-r-xl">
            <h4 className="font-bold text-slate-900 dark:text-white text-sm uppercase">
              Stany magazynowe to świętość
            </h4>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              Nieaktualne stany to główny czynnik niszczący zaufanie (65%). Zintegruj swój magazyn z
              siecią.
            </p>
          </div>
          <div className="bg-slate-100 dark:bg-slate-900 border-l-4 border-gold-400 dark:border-gold-500 p-5 rounded-r-xl">
            <h4 className="font-bold text-slate-900 dark:text-white text-sm uppercase">
              Lojalność jest niska = Szansa jest duża
            </h4>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              Klienci nie są przywiązani do szyldu (85% kupi gdziekolwiek). To Twoja szansa na
              przejęcie klientów od gigantów.
            </p>
          </div>
        </div>

        <div className="mt-12 p-8 bg-gradient-to-b from-slate-100 to-white dark:from-slate-800 dark:to-slate-900 rounded-2xl border border-gold-200 dark:border-gold-500/20">
          <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-4">
            Pokaż się tysiącom klientów
          </h3>
          <p className="text-slate-500 dark:text-slate-400 mb-8 max-w-2xl mx-auto">
            SprawdzonaBron.pl to miejsce, gdzie klienci szukają dostępności. Dołącz do naszej bazy i
            przestań być niewidocznym.
          </p>
          <div className="flex justify-center">
            <Link href="/" className="inline-flex items-center gap-2 bg-gold-500 hover:bg-gold-400 text-slate-900 font-bold py-4 px-8 rounded-xl shadow-lg shadow-gold-500/20 transition-all hover:scale-105" onClick={() => ctaAnalyticalEvent("add-shop")}>
              <Icons.Shop />
              <span>Dodaj swój sklep (za darmo)</span>
            </Link>
          </div>
        </div>
      </div>
}];
export const ReportWizard = () => {
  const $ = _c(65);
  const [activeTab, setActiveTab] = useState("shooters");
  const [currentStep, setCurrentStep] = useState(0);
  const postReportTypeSwitch = useAnalyticsEvent(EventEnum.ReportTypeSwitcher);
  const postReportTypeSwitchButton = useAnalyticsEvent(EventEnum.ReportTypeSwitcherButton);
  const postReportNextButton = useAnalyticsEvent(EventEnum.ReportNextButton);
  const postReportBackButton = useAnalyticsEvent(EventEnum.ReportBackButton);
  const postReportCTA = useAnalyticsEvent(EventEnum.ReportCTA);
  let t0;
  if ($[0] !== activeTab || $[1] !== postReportCTA) {
    t0 = activeTab === "shooters" ? getShooterSteps(postReportCTA) : getShopSteps(postReportCTA);
    $[0] = activeTab;
    $[1] = postReportCTA;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const steps = t0;
  let t1;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setCurrentStep(0);
      window.scrollTo({
        top: 0,
        behavior: "smooth"
      });
    };
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== activeTab) {
    t2 = [activeTab];
    $[4] = activeTab;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  useEffect(t1, t2);
  let t3;
  if ($[6] !== activeTab || $[7] !== currentStep || $[8] !== postReportNextButton || $[9] !== steps.length) {
    t3 = () => {
      if (currentStep < steps.length - 1) {
        postReportNextButton(activeTab);
        setCurrentStep(_temp);
        window.scrollTo({
          top: 0,
          behavior: "smooth"
        });
      }
    };
    $[6] = activeTab;
    $[7] = currentStep;
    $[8] = postReportNextButton;
    $[9] = steps.length;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const nextStep = t3;
  let t4;
  if ($[11] !== activeTab || $[12] !== currentStep || $[13] !== postReportBackButton) {
    t4 = () => {
      if (currentStep > 0) {
        postReportBackButton(activeTab);
        setCurrentStep(_temp2);
        window.scrollTo({
          top: 0,
          behavior: "smooth"
        });
      }
    };
    $[11] = activeTab;
    $[12] = currentStep;
    $[13] = postReportBackButton;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const prevStep = t4;
  const progressPercentage = (currentStep + 1) / steps.length * 100;
  const t5 = `absolute top-1 bottom-1 w-[calc(50%-7px)] bg-white dark:bg-slate-700 rounded-lg transition-all duration-300 shadow-sm ${activeTab === "shooters" ? "left-1" : "left-[calc(50%+4px)]"}`;
  let t6;
  if ($[15] !== t5) {
    t6 = <div className={t5} />;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== postReportTypeSwitch) {
    t7 = () => {
      setActiveTab("shooters");
      postReportTypeSwitch("shooters");
    };
    $[17] = postReportTypeSwitch;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const t8 = `relative z-10 px-4 py-2 rounded-lg text-xs md:text-sm font-bold transition-colors w-36 md:w-40 ${activeTab === "shooters" ? "text-slate-900 dark:text-white" : "text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-white"}`;
  let t9;
  if ($[19] !== t7 || $[20] !== t8) {
    t9 = <button onClick={t7} className={t8}>Dla strzelców i entuzjastów</button>;
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== postReportTypeSwitch) {
    t10 = () => {
      setActiveTab("shops");
      postReportTypeSwitch("shops");
    };
    $[22] = postReportTypeSwitch;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  const t11 = `relative z-10 px-4 py-2 rounded-lg text-xs md:text-sm font-bold transition-colors w-36 md:w-40 flex items-center justify-center gap-2 ${activeTab === "shops" ? "text-gold-600 dark:text-gold-400" : "text-slate-500 dark:text-slate-400 hover:text-gold-500 dark:hover:text-gold-400"}`;
  let t12;
  if ($[24] !== t10 || $[25] !== t11) {
    t12 = <button onClick={t10} className={t11}>Dla właścicieli sklepów</button>;
    $[24] = t10;
    $[25] = t11;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== t12 || $[28] !== t6 || $[29] !== t9) {
    t13 = <div className="flex justify-center mb-8"><div className="bg-slate-200 dark:bg-slate-800 p-1 rounded-xl inline-flex relative shadow-inner">{t6}{t9}{t12}</div></div>;
    $[27] = t12;
    $[28] = t6;
    $[29] = t9;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  const t14 = currentStep + 1;
  let t15;
  if ($[31] !== steps.length || $[32] !== t14) {
    t15 = <span>KROK {t14} z {steps.length}</span>;
    $[31] = steps.length;
    $[32] = t14;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  const t16 = steps[currentStep];
  let t17;
  if ($[34] !== t16.title) {
    t17 = <span>{t16.title}</span>;
    $[34] = t16.title;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] !== t15 || $[37] !== t17) {
    t18 = <div className="flex justify-between text-xs text-slate-500 dark:text-slate-500 mb-2 font-mono">{t15}{t17}</div>;
    $[36] = t15;
    $[37] = t17;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  const t19 = `${progressPercentage}%`;
  let t20;
  if ($[39] !== t19) {
    t20 = <div className="w-full bg-slate-200 dark:bg-slate-800 rounded-full h-1.5"><div className="h-1.5 rounded-full bg-gradient-to-r from-brand-500 to-brand-700 dark:from-brand-600 dark:to-brand-400 transition-all duration-500 ease-out" style={{
        width: t19
      }} /></div>;
    $[39] = t19;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  let t21;
  if ($[41] !== t18 || $[42] !== t20) {
    t21 = <div className="mb-8">{t18}{t20}</div>;
    $[41] = t18;
    $[42] = t20;
    $[43] = t21;
  } else {
    t21 = $[43];
  }
  const t22 = `${activeTab}-${currentStep}`;
  const t23 = steps[currentStep];
  let t24;
  if ($[44] !== t22 || $[45] !== t23.content) {
    t24 = <div className="min-h-[400px] mb-8 relative"><div key={t22} className="animate-slide-up">{t23.content}</div></div>;
    $[44] = t22;
    $[45] = t23.content;
    $[46] = t24;
  } else {
    t24 = $[46];
  }
  const t25 = currentStep === 0;
  const t26 = `px-6 py-3 rounded-lg font-bold text-sm transition-colors ${currentStep === 0 ? "text-slate-400 dark:text-slate-600 cursor-not-allowed" : "text-slate-600 dark:text-white hover:bg-slate-200 dark:hover:bg-slate-800"}`;
  let t27;
  if ($[47] !== prevStep || $[48] !== t25 || $[49] !== t26) {
    t27 = <button onClick={prevStep} disabled={t25} className={t26}>← Wstecz</button>;
    $[47] = prevStep;
    $[48] = t25;
    $[49] = t26;
    $[50] = t27;
  } else {
    t27 = $[50];
  }
  let t28;
  if ($[51] !== activeTab || $[52] !== currentStep || $[53] !== nextStep || $[54] !== postReportTypeSwitchButton || $[55] !== steps.length) {
    t28 = currentStep < steps.length - 1 ? <button onClick={nextStep} className="px-8 py-3 bg-slate-900 text-white dark:bg-white dark:text-slate-900 rounded-lg font-bold text-sm hover:bg-slate-700 dark:hover:bg-slate-200 transition-colors shadow-lg shadow-slate-900/10 dark:shadow-white/10">Dalej →</button> : <button onClick={() => {
      postReportTypeSwitchButton(activeTab === "shooters" ? "shops" : "shooters");
      setActiveTab(activeTab === "shooters" ? "shops" : "shooters");
    }} className="px-8 py-3 bg-brand-600 text-white rounded-lg font-bold text-sm hover:bg-brand-500 transition-colors shadow-lg shadow-brand-500/20">{activeTab === "shooters" ? "Zobacz raport dla Sklep\xF3w \u2192" : "Zobacz raport dla Strzelc\xF3w \u2192"}</button>;
    $[51] = activeTab;
    $[52] = currentStep;
    $[53] = nextStep;
    $[54] = postReportTypeSwitchButton;
    $[55] = steps.length;
    $[56] = t28;
  } else {
    t28 = $[56];
  }
  let t29;
  if ($[57] !== t27 || $[58] !== t28) {
    t29 = <div className="flex justify-between items-center border-t border-slate-200 dark:border-slate-800 pt-6">{t27}{t28}</div>;
    $[57] = t27;
    $[58] = t28;
    $[59] = t29;
  } else {
    t29 = $[59];
  }
  let t30;
  if ($[60] !== t13 || $[61] !== t21 || $[62] !== t24 || $[63] !== t29) {
    t30 = <>{t13}{t21}{t24}{t29}</>;
    $[60] = t13;
    $[61] = t21;
    $[62] = t24;
    $[63] = t29;
    $[64] = t30;
  } else {
    t30 = $[64];
  }
  return t30;
};
function _temp(prev) {
  return prev + 1;
}
function _temp2(prev_0) {
  return prev_0 - 1;
}